import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import type {
  PatientInboundFaxT,
  PatientOutboundFaxT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { WorkspaceSection } from '@/shared/ui/primitives';
import { formatPhoneNumber } from '@/shared/lib/formatters';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';

import {
  usePatientInboundFaxesQuery,
  usePatientOutboundFaxesQuery,
} from '../model/usePatientFaxesQuery';
import {
  DEFAULT_FAX_TITLE,
  DocumentDraftCard,
  FAX_DRAFT_STORE,
  useDocumentDraftsQuery,
  type DocumentDraft,
} from '@/entities/document-draft';
import { openFaxEditor } from '@/shared/stores';
import { DocumentCard } from '@/shared/ui/document-editor';
import styles from './PatientFaxTab.module.css';

const FAX_DIRECTIONS = [
  { id: 'drafts', label: 'Drafts', emptyLabel: 'No fax drafts' },
  { id: 'outbound', label: 'Sent', emptyLabel: 'No sent faxes' },
  { id: 'inbound', label: 'Received', emptyLabel: 'No received faxes' },
] as const;

type FaxDirection = (typeof FAX_DIRECTIONS)[number]['id'];
type Fax = PatientOutboundFaxT | PatientInboundFaxT;
type SelectedFax = { direction: FaxDirection; fax: Fax };

interface PatientFaxTabProps {
  userId?: string;
  onCreate?: () => void;
}

function formatFaxDate(value: string | null) {
  if (!value) {
    return 'Date unavailable';
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatBytes(value: number | null | undefined) {
  if (!value) {
    return 'Unknown size';
  }
  if (value < 1024) {
    return `${value} B`;
  }
  return `${(value / 1024).toFixed(1)} KB`;
}

function isOutboundFax(fax: Fax): fax is PatientOutboundFaxT {
  return 'destinationFaxNumber' in fax;
}

function faxTitle(fax: Fax) {
  if (isOutboundFax(fax)) {
    return `Outbound fax to ${fax.destinationFaxNumber}`;
  }

  const subject = fax.subject?.trim();
  const originatingFaxNumber = formatPhoneNumber(fax.originatingFaxNumber);
  if (subject) {
    return subject;
  }
  if (originatingFaxNumber) {
    return `Inbound fax from ${originatingFaxNumber}`;
  }
  return 'Inbound fax';
}

function faxSubtitle(fax: Fax) {
  if (isOutboundFax(fax)) {
    return null;
  }
  const originatingFaxNumber = formatPhoneNumber(fax.originatingFaxNumber);
  if (!fax.subject?.trim() || !originatingFaxNumber) {
    return null;
  }
  return `From ${originatingFaxNumber}`;
}

function faxDate(fax: Fax) {
  return formatFaxDate(isOutboundFax(fax) ? fax.createdAt : (fax.downloadedAt ?? fax.createdAt));
}

function faxStatus(fax: Fax) {
  return fax.status.replaceAll('_', ' ');
}

function DocumentPreview({ fax }: { fax: Fax }) {
  return (
    <div className={styles.documentViewer}>
      <iframe
        src={`${fax.documentUrl}#page=1&toolbar=1&navpanes=1&zoom=page-fit`}
        title={`${faxTitle(fax)} preview`}
        className={styles.documentFrame}
        loading="lazy"
      />
    </div>
  );
}

function FaxCard({
  direction,
  fax,
  onSelect,
}: {
  direction: FaxDirection;
  fax: Fax;
  onSelect: (fax: SelectedFax) => void;
}) {
  const title = faxTitle(fax);
  const subtitle = faxSubtitle(fax);
  return (
    <DocumentCard
      title={title}
      details={subtitle ? [subtitle, faxDate(fax)] : [faxDate(fax)]}
      previewUrl={fax.documentUrl || null}
      ariaLabel={`View ${title}`}
      onSelect={() => onSelect({ direction, fax })}
    />
  );
}

export function PatientFaxTab({ userId, onCreate }: PatientFaxTabProps) {
  const [direction, setDirection] = useState<FaxDirection>('outbound');
  const [selectedFax, setSelectedFax] = useState<SelectedFax | null>(null);
  const draftsQuery = useDocumentDraftsQuery(userId, FAX_DRAFT_STORE);
  const outboundQuery = usePatientOutboundFaxesQuery(userId);
  const inboundQuery = usePatientInboundFaxesQuery(userId);
  const selectedTitle = useMemo(
    () => (selectedFax ? faxTitle(selectedFax.fax) : 'Fax details'),
    [selectedFax]
  );

  return (
    <WorkspaceSection
      title="Faxes"
      action={
        <Button
          type="button"
          size="sm"
          onClick={() => {
            if (onCreate) {
              onCreate();
            } else if (userId) {
              openFaxEditor(userId);
            }
          }}
        >
          <Plus className={styles.createIcon} aria-hidden="true" />
          Create
        </Button>
      }
    >
      <Tabs
        value={direction}
        onValueChange={(value) => setDirection(value as FaxDirection)}
        className={styles.tabs}
      >
        <TabsList aria-label="Fax direction">
          {FAX_DIRECTIONS.map(({ id, label }) => (
            <TabsTrigger key={id} value={id}>
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {FAX_DIRECTIONS.map(({ id, emptyLabel }) => {
          const query =
            id === 'drafts' ? draftsQuery : id === 'outbound' ? outboundQuery : inboundQuery;
          const faxes = query.data ?? [];

          return (
            <TabsContent key={id} value={id}>
              {query.isLoading ? (
                <p className={styles.emptyState} role="status">
                  Loading faxes...
                </p>
              ) : query.isError ? (
                <p className={styles.emptyState} role="status">
                  Unable to load faxes
                </p>
              ) : faxes.length === 0 ? (
                <p className={styles.emptyState} role="status">
                  {emptyLabel}
                </p>
              ) : (
                <div className={styles.faxList}>
                  {id === 'drafts' && userId
                    ? (faxes as DocumentDraft[]).map((draft) => (
                        <DocumentDraftCard
                          key={draft.id}
                          patientId={userId}
                          draft={draft}
                          defaultTitle={DEFAULT_FAX_TITLE}
                          onSelect={() => userId && openFaxEditor(userId, { draftId: draft.id })}
                        />
                      ))
                    : (faxes as Fax[]).map((fax) => (
                        <FaxCard key={fax.id} direction={id} fax={fax} onSelect={setSelectedFax} />
                      ))}
                </div>
              )}
            </TabsContent>
          );
        })}
      </Tabs>

      <Dialog open={selectedFax !== null} onOpenChange={(open) => !open && setSelectedFax(null)}>
        <DialogContent className={styles.dialog}>
          <DialogHeader>
            <DialogTitle>{selectedTitle}</DialogTitle>
            <DialogDescription>
              {selectedFax ? `${faxDate(selectedFax.fax)} · ${faxStatus(selectedFax.fax)}` : null}
            </DialogDescription>
          </DialogHeader>
          {selectedFax && userId ? (
            <div className={styles.detailsLayout}>
              <div className={styles.documentSection}>
                <DocumentPreview fax={selectedFax.fax} />
              </div>
              <div className={styles.detailsAside}>
                <p className={styles.documentHeading}>Fax information</p>
                <div className={styles.detailGrid}>
                  <span>Direction</span>
                  <strong>{selectedFax.direction === 'outbound' ? 'Outbound' : 'Inbound'}</strong>
                  <span>Status</span>
                  <strong>{faxStatus(selectedFax.fax)}</strong>
                  <span>File size</span>
                  <strong>{formatBytes(selectedFax.fax.documentByteLength)}</strong>
                  <span>Provider fax ID</span>
                  <strong>
                    {isOutboundFax(selectedFax.fax)
                      ? (selectedFax.fax.providerFaxId ?? 'Not assigned')
                      : selectedFax.fax.providerFaxId}
                  </strong>
                  {!isOutboundFax(selectedFax.fax) && selectedFax.fax.originatingFaxNumber && (
                    <>
                      <span>Sender fax number</span>
                      <strong>{formatPhoneNumber(selectedFax.fax.originatingFaxNumber)}</strong>
                    </>
                  )}
                  {isOutboundFax(selectedFax.fax) && selectedFax.fax.errorMessage && (
                    <>
                      <span>Error</span>
                      <strong>{selectedFax.fax.errorMessage}</strong>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </WorkspaceSection>
  );
}
